import classNames from "classnames";
import { FormField } from "components/Forms/FormField";
import { defaultSize } from "components/Lyne/LyneConstants";
import { LyneFormField } from "components/Lyne/LyneFormField";
import { createLogger } from "logging";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

import { HookFormError } from "./HookFormError";

const logger = createLogger("HookForm:Input");

interface HookFormInputProps<TFieldValues extends FieldValues, TName extends Path<TFieldValues>> extends FormField {
  name: TName;
  control: Control<TFieldValues, unknown, TFieldValues>;
  minLength?: number;
  maxLength?: number;
  size?: "s" | "m" | "l";
}

export const HookFormInput = <TFieldValues extends FieldValues, TName extends Path<TFieldValues>>(
  props: HookFormInputProps<TFieldValues, TName>
) => {
  const { name, control, maxLength, minLength, size = defaultSize, required, label, contextHelp } = props;

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required }}
      render={({ field, fieldState }) => {
        const { onChange, value, ref, onBlur, disabled } = field;

        const { isDirty, isTouched, error } = fieldState;
        const className = classNames(
          isDirty ? "is-dirty" : "",
          isTouched ? "is-touched" : "",
          error ? "is-error" : "",
          required && (isTouched || isDirty) && !value ? "sbb-invalid" : ""
        );

        return (
          <LyneFormField size={size} name={name} required={required} label={label} contextHelp={contextHelp}>
            <input
              className={className}
              ref={ref}
              name={name}
              value={value}
              disabled={disabled}
              minLength={minLength}
              maxLength={maxLength}
              autoComplete="off"
              onBlur={(e) => {
                logger.debug(`${name}.onBlur(): `, e.target.value);
                onBlur();
              }}
              onChange={(e) => {
                logger.debug(`${name}.onChange(): `, e.target.value);
                onChange(e);
              }}
            />
            <HookFormError error={error} />
          </LyneFormField>
        );
      }}
    />
  );
};
